/* -----------------------------------------------------------------------------------------------------
  - Created: [11-03-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD]
  - Description: This file contains the Reset Password Form
  ------------------------------------------------------------------------------------------------------- */

import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import './resetPasswordFormStyle.css';
import { VscEyeClosed } from 'react-icons/vsc';
import { RxEyeOpen } from 'react-icons/rx';
import CustomTextFieldWithIcon from '../../../custom_components/custom_text_field/CustomTextFieldWithIcon';
import { CiLock } from 'react-icons/ci';
import ActionButton from '../../../custom_components/CustomButtons/ActionButton/ActionButton';
import { Grid } from '@mui/joy';
import PasswordStrengthBar from 'react-password-strength-bar';
import { reset_Password } from '../../../../config/ProfileContextApis/resetPasswordApi';
import { ErrorToast, SuccessToast } from '../../../../utils/toast';
import check_mark_icon from '../../../../Assets/CommonImages/check_mark_icon.png'
import check_mark_grey from '../../../../Assets/CommonImages/check_mark_grey.png'

function ResetPasswordForm({ userData, handleClose }) {
    const [showCurrentPasswod, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false); // Disable button on submission

    // Toggle password visibility
    const handleCurrentPasswordVisibility = () => {
        setShowCurrentPassword(!showCurrentPasswod);
    };
    const handlePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    // Formik setup for validation and form submission
    const formik = useFormik({
        initialValues: {
            current_password: '',
            newPassword: '',
            password: '',
        },
        validationSchema: Yup.object({
            current_password: Yup.string()
                .required('Current Password is required'),

            newPassword: Yup.string()
                .required('Confirm New Password is required')
                .required('New Password is required')
                .min(8, 'Password must be at least 8 characters long')
                .max(20, 'Password must not exceed 20 characters')
                .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
                .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
                .matches(/[0-9]/, 'Password must contain at least one digit')
                .matches(/[@$!%*#?&]/, 'Password must contain at least one special character'),

            password: Yup.string()
                .required('Confirm New Password is required')
                .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
        }),
        onSubmit: async (values) => {

            const params = {
                userId: userData?.id || '',
                password: values?.password,
                current_password: values?.current_password,
            }

            try {
                setIsButtonDisabled(true);
                // API call to reset password
                const response = await reset_Password(params);

                if (response?.data?.status === 'success') {
                    SuccessToast(response?.data?.message);
                    handleClose()
                } else {
                    ErrorToast(response?.message || 'Failed to reset password');
                }
                setIsButtonDisabled(false);
            } catch (error) {
                setIsButtonDisabled(false);
            }
        },
    });


    const conditions = [
        { text: 'Password must be at least 8 characters', isValid: formik.values.password.length >= 8 },
        { text: 'Password must contain at least one lowercase letter', isValid: /[a-z]/.test(formik.values.password) },
        { text: 'Password must contain at least one uppercase letter', isValid: /[A-Z]/.test(formik.values.password) },
        { text: 'Password must contain at least one digit', isValid: /[0-9]/.test(formik.values.password) },
        { text: 'Password must contain at least one special character', isValid: /[@$!%*#?&]/.test(formik.values.password) },
    ];

    return (
        <Grid container spacing={3} alignItems="center" justifyContent="center">
            <Grid item xs={12} lg={12}> {/* Grid item with responsive sizing */}

                <div className="reset-password-box">


                    {/* New Current Field */}
                    <div className="input-container">
                        <span className='reset-form-label'>Current  Password</span>
                        <CustomTextFieldWithIcon
                            type={showCurrentPasswod ? 'text' : 'password'}
                            id="current_password"
                            name="current_password"
                            placeholder="Enter your Current password"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.current_password}
                            StartIcon={CiLock}
                            EndIcon={showPassword ? RxEyeOpen : VscEyeClosed}
                            onEndIconClick={handleCurrentPasswordVisibility}
                            error={formik.touched.current_password && Boolean(formik.errors.current_password)}
                            helperText={formik.touched.current_password && formik.errors.current_password}
                        />
                    </div>



                    {/* New Password Field */}
                    <div className="input-container">
                        <span className='reset-form-label'>New  Password</span>
                        <CustomTextFieldWithIcon
                            type={showPassword ? 'text' : 'password'}
                            id="newPassword"
                            name="newPassword"
                            placeholder="Enter your new password"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.newPassword}
                            StartIcon={CiLock}
                            EndIcon={showPassword ? RxEyeOpen : VscEyeClosed}
                            onEndIconClick={handlePasswordVisibility}
                            error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                            helperText={formik.touched.newPassword && formik.errors.newPassword}
                        />
                        <PasswordStrengthBar password={formik.values.newPassword} />

                    </div>

                    {/* Confirm New Password Field */}
                    <div className="input-container">
                        <span className='reset-form-label'> Confirm Password</span>
                        <CustomTextFieldWithIcon
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="password"
                            name="password"
                            placeholder="Confirm your new password"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.password}
                            StartIcon={CiLock}
                            EndIcon={showConfirmPassword ? RxEyeOpen : VscEyeClosed}
                            onEndIconClick={handleConfirmPasswordVisibility}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                        />
                    </div>
                    {/* VALIDAITON WARINIGN LABELS */}
                    {/* <div>
                        <div className='password-info-section'>
                            <img src={check_mark_icon} alt="right_icon" style={{ width: 20 }} />
                            <span className='password-info-text'>Password must be at least 8 characters</span>
                        </div>
                        <div className='password-info-section'>
                            <img src={check_mark_icon} alt="right_icon" style={{ width: 20 }} />
                            <span className='password-info-text'>New password must not be the same as the current password</span>
                        </div>

                        <div className='password-info-section'>
                            <img src={check_mark_icon} alt="right_icon" style={{ width: 20 }} />
                            <span className='password-info-text'>Password must contain at least one lowercase letter</span>
                        </div>
                        <div className='password-info-section'>
                            <img src={check_mark_icon} alt="right_icon" style={{ width: 20 }} />
                            <span className='password-info-text'>Password must contain at least one uppercase letter</span>
                        </div>
                        <div className='password-info-section'>
                            <img src={check_mark_icon} alt="right_icon" style={{ width: 20 }} />
                            <span className='password-info-text'>Password must contain at least one digit</span>
                        </div>
                        <div className='password-info-section'>
                            <img src={check_mark_icon} alt="right_icon" style={{ width: 20 }} />
                            <span className='password-info-text'>Password must contain at least one special character</span>
                        </div>
                    </div> */}

                    <div>
                        {conditions?.map((condition, index) => (
                            <div className="password-info-section" key={index}>
                                <img
                                    src={condition.isValid ? check_mark_icon : check_mark_grey}
                                    alt="status_icon"
                                    style={{ width: 20 }}
                                />
                                <span className="password-info-text">{condition.text}</span>
                            </div>
                        ))}
                    </div>
                    {/* Action Buttons */}
                    <div className="form-bottom-action-buttons-container">
                        <ActionButton
                            onClick={() => formik.resetForm()}
                            label='Reset'
                            btnType='outlined'
                        />
                        <ActionButton
                            onClick={() => formik.handleSubmit()}
                            btnDisable={isButtonDisabled}
                            label='Save'
                            btnType='primary'
                        />
                    </div>
                </div>

            </Grid>
        </Grid>

    );
}

export default ResetPasswordForm;

